import { useEffect, useRef } from 'react';

// Open dialogs, oldest first. Only the LAST one (the topmost) answers Escape.
const openDialogs: symbol[] = [];

/**
 * Close this dialog on Escape — but only when it is the TOPMOST open dialog.
 * Stacked dialogs (a document preview over its detail modal) each used to
 * listen on window/document, so one Escape closed both. Every dialog that
 * closes on Escape uses this hook instead of its own listener: each registers
 * on mount (so a dialog opened later sits above), and a handled Escape is
 * marked (preventDefault) so no other dialog acts on the same key press even
 * if the stack changes mid-dispatch. `onClose` may change every render.
 */
export function useEscapeToClose(onClose: () => void, enabled = true): void {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!enabled) return;
    const id = Symbol('dialog');
    openDialogs.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (openDialogs[openDialogs.length - 1] !== id) return;
      e.preventDefault();
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const at = openDialogs.lastIndexOf(id);
      if (at !== -1) openDialogs.splice(at, 1);
    };
  }, [enabled]);
}
